import { provideHttpClient, withFetch, withInterceptors } from '@angular/common/http';
import { ApplicationConfig, inject, provideAppInitializer } from '@angular/core';
import { provideAnimations } from '@angular/platform-browser/animations';
import { provideRouter } from '@angular/router';
import { MSAL_INSTANCE } from '@azure/msal-angular';
import { BrowserCacheLocation, LogLevel, PublicClientApplication } from '@azure/msal-browser';
import { FetchTransport, getWebInstrumentations, initializeFaro } from '@grafana/faro-web-sdk';
import { provideMatomo, withRouteData, withRouter } from 'ngx-matomo-client';
import { MessageService } from 'primeng/api';
import { providePrimeNG } from 'primeng/config';
import { environment } from 'src/environments/environment';
import { fmcTheme } from 'src/styles/primeng-fmc-theme';

import { APP_ROUTES } from './app.routing';
import { authErrorInterceptor } from './shared/interceptors/auth-error.interceptor';
import { debugInterceptor } from './shared/interceptors/debug.interceptor';
import { AppConfigService } from './shared/services/app.config.service';

export const appConfig: ApplicationConfig = {
  providers: [
    provideRouter(
      APP_ROUTES,
      // uncomment the line below to enable debug tracing for the router
      // withDebugTracing(),
    ),
    provideAnimations(),
    providePrimeNG({
      theme: {
        options: { darkModeSelector: false },
        preset: fmcTheme,
      },
    }),
    provideAppInitializer(() => {
      const appConfigService = inject(AppConfigService);
      appConfigService.loadAppConfigData();
    }),

    ...(environment.enableLogging
      ? [
          provideMatomo(
            {
              siteId: environment.siteId,
              trackerUrl: environment.matomoUrl,
            },
            withRouter(),
            withRouteData(),
          ),
          provideAppInitializer(() => {
            initializeFaro({
              app: {
                name: 'client',
                environment: environment.env,
                // namespace is probably needed if we use traces
                // namespace: environment.env,
                version: environment.version,
              },

              transports: [
                new FetchTransport({
                  url: environment.alloyHost,
                  apiKey: environment.faroKey,
                }),
              ],
              instrumentations: [
                ...getWebInstrumentations({
                  captureConsole: true,
                  enablePerformanceInstrumentation: true,
                }),
              ],
              trackResources: true,

              consoleInstrumentation: {
                disabledLevels: [],
              },
            });
          }),
        ]
      : []),

    // This is needed for the toast messages in the api errors
    MessageService,

    // only get good errors on not-prod
    ...(!environment.production
      ? [provideHttpClient(withFetch(), withInterceptors([authErrorInterceptor, debugInterceptor]))]
      : [provideHttpClient(withFetch(), withInterceptors([authErrorInterceptor]))]),

    // Setup MSAL instance for SSO login
    // This part resolves the promise for setup
    provideAppInitializer(() => {
      const msalInstance = inject(MSAL_INSTANCE);
      return msalInstance.initialize();
    }),
    // This part provides the setup data for the MSAL instance
    {
      provide: MSAL_INSTANCE,
      useFactory: () => {
        return new PublicClientApplication({
          auth: {
            clientId: environment.msalClientId,
            authority: environment.msalIssuer,
            redirectUri: window.location.origin + '/client/login/callback',
          },
          cache: {
            cacheLocation: BrowserCacheLocation.SessionStorage,
          },
          system: {
            allowPlatformBroker: false, // Disables WAM Broker
            loggerOptions: {
              loggerCallback: (logLevel: LogLevel, message: string) => console.log(logLevel, message),
              logLevel: LogLevel.Info,
              piiLoggingEnabled: false,
            },
          },
        });
      },
    },
  ],
};
